import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../context/DriverAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Truck } from 'lucide-react';

import { getApiUrl, isBackendConfigured } from '../config';

const isAtLeast21 = (dateOfBirth) => {
  if (!dateOfBirth) return false;
  const dob = new Date(`${dateOfBirth}T12:00:00`);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 21;
};

const DriverSignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useDriverAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [confirmAge21, setConfirmAge21] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (!confirmAge21) {
      setError('You must confirm that you are 21 or older to drive for Tribal Dash.');
      return;
    }

    if (!isAtLeast21(dateOfBirth)) {
      setError('You must be at least 21 years old to sign up as a driver.');
      return;
    }

    setLoading(true);

    if (!isBackendConfigured()) {
      setError('Backend URL is not configured. Set REACT_APP_BACKEND_URL in your environment.');
      setLoading(false);
      return;
    }

    const result = await signup({
      email,
      password,
      name,
      address,
      phone,
      dateOfBirth,
      confirmAge21: true,
    });
    setLoading(false);

    if (result.success) {
      navigate(result.driver?.agreementSignedAt ? '/driver/dashboard' : '/driver/agreement');
    } else {
      setError(result.error);
    }
  };

  const maxDob = new Date();
  maxDob.setFullYear(maxDob.getFullYear() - 21);
  const maxDobStr = maxDob.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-neutral-950 rounded-full">
              <Truck className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">Become a Driver</CardTitle>
          <p className="text-gray-600 mt-2">
            Join Tribal Dash delivery partners. Drivers must be 21+.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="signup-name">Full legal name</Label>
              <Input
                id="signup-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-address">Address</Label>
              <Input
                id="signup-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-phone">Phone</Label>
              <Input
                id="signup-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-dob">Date of birth</Label>
              <Input
                id="signup-dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={maxDobStr}
                required
              />
              <p className="text-xs text-gray-500 mt-1">You must be 21 or older.</p>
            </div>
            <div>
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <Checkbox
                id="confirm-age"
                checked={confirmAge21}
                onCheckedChange={(checked) => setConfirmAge21(checked === true)}
              />
              <Label htmlFor="confirm-age" className="text-sm leading-relaxed cursor-pointer">
                I confirm that I am at least 21 years old and eligible to deliver cannabis in New
                York.
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full bg-neutral-950 hover:bg-black"
              size="lg"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create driver account'}
            </Button>

            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/driver/login" className="font-semibold text-neutral-900 underline">
                Log in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverSignupPage;
